import {
  AuthenticityMethod,
  CategoryType,
  type ProductDetails,
  SalesMethod,
} from "mws-ts-v2-sdk";
import type { SaleEvent } from "schema-dts";

import { MediaContentTypeV2 } from "~/types/Catalog";

/**
 * This composable is used to generate schema.org structured data for a product.
 */
export default function useProductSchemaOrg(
  productParam: MaybeRefOrGetter<ProductDetails>,
) {
  const platformStore = usePlatformStore();
  const platformName = platformStore.getPlatformName;

  const product = toValue(productParam);

  const isFootballShirt = product?.type === "football_shirt";

  const productAthlete = filterCategory(product, CategoryType.Athlete);
  const productTeam = filterCategory(product, CategoryType.Team);

  const shirtState = (() => {
    if (product.labels.some((l) => l.text === "Worn")) {
      return "worn";
    }

    if (product.labels.some((l) => l.text === "Issued")) {
      return "issued";
    }

    return undefined;
  })();

  // Only show schema.org structured data for products with certain features
  // so we don't have to have a lot of conditional logic in the schema.org template.
  const showSchemaOrg =
    product.salesMethod === SalesMethod.Bidding &&
    isFootballShirt &&
    !!productTeam &&
    !!productAthlete &&
    (shirtState === "worn" || shirtState === "issued") &&
    product.authenticityMethod === AuthenticityMethod.Fabricks;

  if (!showSchemaOrg) return;

  const route = useRoute();
  const fullProductUrl = `https://${platformStore.getPlatformConfiguration.primaryDomain}${route.path}`;
  const platformOrganisation = useSchemaOrgPlatformOrganisation();

  const productImageUrl = product?.media.filter(
    (m) => m.type === MediaContentTypeV2.Image && !!m.url,
  )[0]?.url;

  const { t } = useI18n();

  const SALE_EVENT_STRUCTURED_DATA: SaleEvent = {
    "@type": "SaleEvent",
    name: t(
      `product.structuredData.footballShirts.saleEvent.${shirtState}.name`,
      {
        PLACEHOLDER_athleteName: productAthlete.name,
      },
    ),
    description: t(
      `product.structuredData.footballShirts.saleEvent.${shirtState}.description`,
      {
        PLACEHOLDER_athleteName: productAthlete.name,
      },
    ),
    url: fullProductUrl,
    image: productImageUrl ?? undefined,
    startDate: product.startDate.toUTCString(),
    endDate: product.endDate.toUTCString(),
    eventStatus: "https://schema.org/EventScheduled",
    eventAttendanceMode: "https://schema.org/OnlineEventAttendanceMode",
    location: {
      "@type": "VirtualLocation",
      url: fullProductUrl,
    },
    performer: {
      "@type": "PerformingGroup",
      name: platformName,
    },
    organizer: platformOrganisation,
    offers: {
      "@type": "Offer",
      availability: product.inStock
        ? "https://schema.org/InStock"
        : "https://schema.org/SoldOut",
      price: getPriceInCurrency(product.prices, "EUR"),
      validFrom: product.startDate.toUTCString(),
      validThrough: product.endDate.toUTCString(),
      priceCurrency: "EUR",
      url: fullProductUrl,
      seller: {
        "@type": "Organization",
        name: platformName,
      },
    },
  };

  useSchemaOrg([
    SALE_EVENT_STRUCTURED_DATA,
    defineProduct({
      name: t(
        `product.structuredData.footballShirts.product.${shirtState}.name`,
        { PLACEHOLDER_athleteName: productAthlete.name },
      ),
      description: t(
        `product.structuredData.footballShirts.product.${shirtState}.description`,
        {
          PLACEHOLDER_athleteName: productAthlete.name,
          PLACEHOLDER_teamName: productTeam.name,
        },
      ),
      url: fullProductUrl,
      image: productImageUrl,
      brand: {
        "@type": "Brand",
        name: productTeam.name,
        url: `https://${platformStore.getPlatformConfiguration.primaryDomain}/category/${productTeam.slug}`,
        sameAs: [`https://en.wikipedia.org/wiki/${productTeam.name}`],
      },
      offers: {
        "@type": "Offer",
        priceCurrency: "EUR",
        price: getPriceInCurrency(product.prices, "EUR"),
        availability: product.inStock
          ? "https://schema.org/InStock"
          : "https://schema.org/SoldOut",
        priceValidUntil: product.endDate,
        validFrom: product.startDate,
        url: fullProductUrl,
        seller: {
          "@type": "Organization",
          name: platformName,
        },
        additionalProperty: [
          {
            "@type": "PropertyValue",
            name: "Player",
            value: productAthlete.name,
            sameAs: `https://en.wikipedia.org/wiki/${productAthlete.name}`,
          },
        ],
      },
    }),
  ]);
}
